import React from 'react'
import {
  Card,
  CardContent,
  Stack,
  Box,
  Typography,
  Chip,
  Avatar,
  useTheme,
} from '@mui/material'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import MedicalServicesIcon from '@mui/icons-material/MedicalServices'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import { getLocation } from 'pages/Jobs/helpers'
import { getSpecialty, getPhysicianProfileImage } from 'pages/Physicians/helpers'
import { formatName, capitalizeFirstLetter } from '../../common/helpers'
import useCandidateFeedback from 'pages/Hospitals/Candidates/AI/CandidateFeedback'

const getMatchTierBackground = (matchTier, theme) => {
  switch (matchTier?.toLowerCase()) {
    case 'excellent': return theme.palette.success.light
    case 'strong': return theme.palette.info.light
    case 'good': return theme.palette.warning.light
    default: return theme.palette.grey[200]
  }
}

const getMatchTierBorderColor = (matchTier, theme) => {
  switch (matchTier?.toLowerCase()) {
    case 'excellent': return theme.palette.success.main
    case 'strong': return theme.palette.info.main
    case 'good': return theme.palette.warning.main
    default: return theme.palette.grey[400]
  }
}

const getMatchTierTextColor = (matchTier, theme) => {
  switch (matchTier?.toLowerCase()) {
    case 'excellent': return theme.palette.success.dark
    case 'strong': return theme.palette.info.dark
    case 'good': return theme.palette.warning.dark
    default: return theme.palette.grey[700]
  }
}

export const ListItemPhysician = ({ data, isActive, onClick }) => {
  const theme = useTheme()
  const location = getLocation(capitalizeFirstLetter(data.city), data.state)
  const subspecialty =
    data.subspecialties && data.subspecialties.length > 0 ? data.subspecialties[0].name : undefined
  const specialty =
    data.specialties.length > 0 ? getSpecialty(data.specialties[0].name, subspecialty) : ''
  const name = formatName(data.user.firstName) + ' ' + formatName(data.user.lastName)
  const image = getPhysicianProfileImage(data.user.profileImage)

  const feedbackElements = useCandidateFeedback({
    candidateId: data.id,
    conversationId: data.conversationId,
    chatbotMessageId: data.chatbotMessageId,
    matchTier: data.matchTier,
    feedback: data.feedback,
  })

  return (
    <Card
      onClick={() => onClick(data.id)}
      sx={{
        borderRadius: 1,
        border: isActive
          ? `2px solid ${theme.palette.primary.main}`
          : `1px solid ${theme.palette.divider}`,
        boxShadow: isActive ? '0 2px 8px rgba(0,0,0,0.12)' : '0 1px 4px rgba(0,0,0,0.06)',
        cursor: 'pointer',
        '&:hover': {
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          borderColor: theme.palette.primary.light,
          transition: 'all 0.2s ease-in-out',
        },
      }}
    >
      <CardContent sx={{ p: { xs: 1.5, md: 2 }, '&:last-child': { pb: { xs: 1.5, md: 2 } } }}>
        <Stack spacing={0.75}>
          {/* Row 1: Avatar + Name + Candidate Type */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
            <Avatar
              src={image}
              alt={name}
              sx={{ width: 36, height: 36, mr: 1 }}
            />
            <Typography
              variant='subtitle1'
              sx={{
                color: theme.palette.primary.dark,
                fontWeight: 'medium',
                fontSize: { xs: '0.9rem', md: '0.95rem' },
                lineHeight: 1.3,
                flex: 1,
                minWidth: 0,
              }}
            >
              {name}
            </Typography>
            {data.candidateType?.name && (
              <Chip
                label={data.candidateType.name}
                size='small'
                variant='outlined'
                sx={{
                  height: '20px',
                  fontSize: '0.65rem',
                  ml: 1,
                  flexShrink: 0,
                  display: { xs: 'none', md: 'flex' },
                }}
              />
            )}
          </Box>

          {/* Row 2: Specialty · Location */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 0.5,
              color: theme.palette.text.secondary,
            }}
          >
            {specialty && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <MedicalServicesIcon sx={{ fontSize: '0.85rem' }} color='action' />
                <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                  {specialty}
                </Typography>
              </Box>
            )}
            {location && (
              <>
                {specialty && (
                  <Typography variant='body2' sx={{ fontSize: '0.7rem', color: theme.palette.grey[400] }}>
                    ·
                  </Typography>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LocationOnIcon sx={{ fontSize: '0.85rem' }} color='action' />
                  <Typography variant='body2' sx={{ fontSize: '0.8rem' }}>
                    {location}
                  </Typography>
                </Box>
              </>
            )}
          </Box>

          {/* Row 3: Specialty chips + Visa chip (desktop only) */}
          {data.specialties?.length > 0 && (
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                flexWrap: 'wrap',
                gap: 0.5,
                alignItems: 'center',
              }}
            >
              {data.specialties.slice(0, 2).map(s => (
                <Chip
                  key={s?.id}
                  label={s?.name}
                  size='small'
                  sx={{ height: '20px', fontSize: '0.65rem' }}
                />
              ))}
              {data.specialties.length > 2 && (
                <Chip
                  label={`+${data.specialties.length - 2}`}
                  size='small'
                  variant='outlined'
                  sx={{ height: '20px', fontSize: '0.65rem' }}
                />
              )}
              {data.visas?.length > 0 && (
                <Chip
                  label='Visa'
                  size='small'
                  variant='outlined'
                  color='success'
                  sx={{ height: '20px', fontSize: '0.65rem' }}
                />
              )}
            </Box>
          )}

          {/* Row 4: AI Match tier + justification + feedback (only present for AI search results) */}
          {data.matchTier && (
            <>
              <Box
                sx={{
                  backgroundColor: getMatchTierBackground(data.matchTier, theme),
                  borderRadius: 1,
                  p: 0.75,
                  borderLeft: `3px solid ${getMatchTierBorderColor(data.matchTier, theme)}`,
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: data.justification ? 0.25 : 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Chip
                      icon={<AutoAwesomeIcon sx={{ fontSize: '0.75rem !important' }} />}
                      label={data.matchTier}
                      size='small'
                      sx={{
                        height: '20px',
                        fontSize: '0.65rem',
                        fontWeight: 'bold',
                        backgroundColor: getMatchTierBorderColor(data.matchTier, theme),
                        color: 'white',
                        '& .MuiChip-icon': { color: 'white' },
                      }}
                    />
                    {data.justification && (
                      <Typography variant='caption' sx={{ color: getMatchTierTextColor(data.matchTier, theme), fontWeight: 600 }}>
                        Why this match
                      </Typography>
                    )}
                  </Box>
                  {feedbackElements?.thumbs}
                </Box>
                {data.justification && (
                  <Typography
                    variant='body2'
                    sx={{
                      fontSize: '0.78rem',
                      color: theme.palette.text.secondary,
                      lineHeight: 1.4,
                    }}
                  >
                    {data.justification}
                  </Typography>
                )}
              </Box>
              {feedbackElements?.panel}
            </>
          )}
        </Stack>
      </CardContent>
    </Card>
  )
}
